import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import useUserAuth from "../../auth/hook/useUserAuth";
import { APIGenerator } from "../../../utils/genericAPI/GenerateAPI";
import { ResponseAPIWithPagination } from "../../../utils/genericAPI/API.types";
import SpendingDemandAPI, { API_BASE_URL_SPENDING_DEMAND_MANAGER } from "../services/SpendingDemandAPI";
import { spendingDemandMapState } from "../redux/spendingDemand.mapstate";
import {
  extractListParams,
  getSpendingDemandListNavigationMode,
  LIST_NAVIGATION_PAGE_PARAM,
  LIST_NAVIGATION_PARAM,
  MANAGER_PARAM_PREFIX,
} from "../utils/spendingDemandListNavigation";

type ListPage = ResponseAPIWithPagination<{ spending_demand_id: string }>;

interface Neighbor {
  id: string;
  page: number;
}

interface Neighbors {
  previous?: Neighbor;
  next?: Neighbor;
  position?: number;
  total?: number;
}

const managerAPI = new APIGenerator<{ spending_demand_id: string }, unknown, unknown>(
  API_BASE_URL_SPENDING_DEMAND_MANAGER
);

// Mêmes valeurs par défaut que useSpendingDemandManager / useSpendingDemandCore
const MANAGER_DEFAULT_PAGE_SIZE = "10";
const MANAGER_DEFAULT_ORDER_BY = "-spending_demand_update_date";
const USER_PAGE_SIZE = 10;
// Les pages voisines ne servent qu'à la navigation : inutile de les garder (et de les refetch) longtemps
const NAVIGATION_GC_TIME = 60 * 1000;

/**
 * Calcule la demande précédente / suivante dans la liste d'origine
 * (contexte transmis dans l'URL par la liste via buildSpendingDemandDetailLink)
 */
export default function useSpendingDemandListNavigation(
  currentId: string | undefined,
  basePath: string
) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { userInfo, addTokenIntheHeaderRequest } = useUserAuth();
  addTokenIntheHeaderRequest();
  const {
    spendingDemand: { filter: userFilter },
  } = useSelector(spendingDemandMapState);

  const mode = getSpendingDemandListNavigationMode(search);
  const userId = userInfo?.user_id;
  const isReady = !!mode && !!currentId && (mode === "manager" || !!userId);

  const listParams = extractListParams(search, mode || "manager");
  const pageParam = mode ? LIST_NAVIGATION_PAGE_PARAM[mode] : "";
  const currentPage = Number(listParams.get(pageParam)) || 1;

  // Contexte de la liste (filtres, tri, recherche) hors numéro de page
  const contextParams = new URLSearchParams(listParams);
  contextParams.delete(LIST_NAVIGATION_PARAM);
  contextParams.delete(pageParam);
  const contextKey = contextParams.toString();

  const fetchPage = (page: number): Promise<ListPage> => {
    if (mode === "manager") {
      const get = (key: string) => listParams.get(MANAGER_PARAM_PREFIX + key);
      return managerAPI.getAll({
        page,
        pageSize: parseInt(get("pageSize") || MANAGER_DEFAULT_PAGE_SIZE),
        filterQuery: get("filter") || "",
        orderBy: get("orderBy") || MANAGER_DEFAULT_ORDER_BY,
        search: get("search") || "",
        descSearch: get("descSearch") || "",
      });
    }
    return SpendingDemandAPI.getFetchAllSpendingDemand(
      USER_PAGE_SIZE,
      page,
      userFilter,
      userId || "null",
      true
    ) as Promise<ListPage>;
  };

  // Préfixes alignés sur les invalidations existantes ("spendingdemandmanager" / "spendingdemand")
  const pageQuery = (page: number, enabled: boolean) => ({
    queryKey: [
      mode === "manager" ? "spendingdemandmanager" : "spendingdemand",
      "listNavigation",
      contextKey,
      mode === "user" ? `${userId}:${JSON.stringify(userFilter)}` : "",
      page,
    ],
    queryFn: () => fetchPage(page),
    enabled: isReady && enabled && page >= 1,
    gcTime: NAVIGATION_GC_TIME,
  });

  const currentQuery = useQuery(pageQuery(currentPage, true));

  const ids = currentQuery.data?.data?.map((item) => item.spending_demand_id) || [];
  const index = currentId ? ids.indexOf(currentId) : -1;
  const pagination = currentQuery.data?.pagination;
  const isMissing = currentQuery.isSuccess && index === -1;

  const needPreviousPage = (index === 0 || isMissing) && currentPage > 1;
  const needNextPage =
    (isMissing || (index >= 0 && index === ids.length - 1)) &&
    !!pagination &&
    currentPage < pagination.total_pages;

  const previousPageQuery = useQuery(pageQuery(currentPage - 1, needPreviousPage));
  const nextPageQuery = useQuery(pageQuery(currentPage + 1, needNextPage));

  const previousPageIds = previousPageQuery.data?.data?.map((item) => item.spending_demand_id) || [];
  const nextPageIds = nextPageQuery.data?.data?.map((item) => item.spending_demand_id) || [];

  // La demande a glissé sur une page voisine (liste modifiée entre-temps) : on recale la page dans l'URL
  const relocatedPage =
    isMissing && currentId
      ? previousPageIds.includes(currentId)
        ? currentPage - 1
        : nextPageIds.includes(currentId)
        ? currentPage + 1
        : undefined
      : undefined;

  useEffect(() => {
    if (!relocatedPage || !mode) return;
    const params = new URLSearchParams(listParams);
    params.set(pageParam, relocatedPage.toString());
    navigate(`${basePath}/${currentId}?${params.toString()}`, { replace: true });
  }, [relocatedPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Garde les voisins connus si la demande sort de la liste après une action
  // (ex: validée alors que la liste est filtrée sur "à traiter")
  const lastNeighbors = useRef<Neighbors>({});

  if (index >= 0) {
    const pageSize = pagination?.page_size || ids.length;
    lastNeighbors.current = {
      previous:
        index > 0
          ? { id: ids[index - 1], page: currentPage }
          : needPreviousPage && previousPageIds.length
          ? { id: previousPageIds[previousPageIds.length - 1], page: currentPage - 1 }
          : undefined,
      next:
        index < ids.length - 1
          ? { id: ids[index + 1], page: currentPage }
          : needNextPage && nextPageIds.length
          ? { id: nextPageIds[0], page: currentPage + 1 }
          : undefined,
      position: (currentPage - 1) * pageSize + index + 1,
      total: pagination?.total_count,
    };
  } else if (isMissing) {
    // La position n'a plus de sens une fois la demande sortie de la liste
    lastNeighbors.current = { ...lastNeighbors.current, position: undefined, total: undefined };
  }

  const neighbors = lastNeighbors.current;

  const goTo = (neighbor?: Neighbor) => {
    if (!neighbor || !mode) return;
    const params = new URLSearchParams(listParams);
    params.set(pageParam, neighbor.page.toString());
    navigate(`${basePath}/${neighbor.id}?${params.toString()}`, { replace: true });
    window.scrollTo({ top: 0 });
  };

  return {
    isEnabled: !!mode,
    position: neighbors.position,
    total: neighbors.total,
    goToPrevious: neighbors.previous ? () => goTo(neighbors.previous) : undefined,
    goToNext: neighbors.next ? () => goTo(neighbors.next) : undefined,
  };
}
